const sanitizeXml = (value) => {
  const str = value === null || value === undefined ? "" : String(value);
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
};

const crcTable = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let j = 0; j < 8; j += 1) {
      c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
    }
    table[i] = c >>> 0;
  }
  return table;
})();

const crc32 = (bytes) => {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = crcTable[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
};

const u16le = (num) => [num & 0xff, (num >>> 8) & 0xff];
const u32le = (num) => [num & 0xff, (num >>> 8) & 0xff, (num >>> 16) & 0xff, (num >>> 24) & 0xff];

const buildZip = (entries) => {
  const files = entries.map((entry) => {
    const nameBytes = new TextEncoder().encode(entry.name);
    const dataBytes = new TextEncoder().encode(entry.data);
    return {
      ...entry,
      nameBytes,
      dataBytes,
      crc: crc32(dataBytes),
    };
  });

  const localParts = [];
  const centralParts = [];
  let offset = 0;

  files.forEach((file) => {
    const localHeader = new Uint8Array([
      ...u32le(0x04034b50),
      ...u16le(20),
      ...u16le(0),
      ...u16le(0),
      ...u16le(0),
      ...u16le(0),
      ...u32le(file.crc),
      ...u32le(file.dataBytes.length),
      ...u32le(file.dataBytes.length),
      ...u16le(file.nameBytes.length),
      ...u16le(0),
      ...file.nameBytes,
    ]);

    localParts.push(localHeader, file.dataBytes);

    const centralHeader = new Uint8Array([
      ...u32le(0x02014b50),
      ...u16le(20),
      ...u16le(20),
      ...u16le(0),
      ...u16le(0),
      ...u16le(0),
      ...u16le(0),
      ...u32le(file.crc),
      ...u32le(file.dataBytes.length),
      ...u32le(file.dataBytes.length),
      ...u16le(file.nameBytes.length),
      ...u16le(0),
      ...u16le(0),
      ...u16le(0),
      ...u16le(0),
      ...u32le(0),
      ...u32le(offset),
      ...file.nameBytes,
    ]);
    centralParts.push(centralHeader);

    offset += localHeader.length + file.dataBytes.length;
  });

  const centralSize = centralParts.reduce((sum, part) => sum + part.length, 0);
  const end = new Uint8Array([
    ...u32le(0x06054b50),
    ...u16le(0),
    ...u16le(0),
    ...u16le(files.length),
    ...u16le(files.length),
    ...u32le(centralSize),
    ...u32le(offset),
    ...u16le(0),
  ]);

  const total = localParts.reduce((sum, part) => sum + part.length, 0)
    + centralSize
    + end.length;

  const result = new Uint8Array(total);
  let cursor = 0;
  [...localParts, ...centralParts, end].forEach((part) => {
    result.set(part, cursor);
    cursor += part.length;
  });

  return result;
};

const colName = (idx) => {
  let n = idx + 1;
  let s = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    s = String.fromCharCode(65 + rem) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
};

const buildSheetXml = (rows) => {
  const headers = Object.keys(rows[0]);
  const allRows = [headers, ...rows.map((row) => headers.map((h) => row[h]))];

  const body = allRows
    .map((row, rowIndex) => {
      const cells = row
        .map((value, colIndex) => {
          const ref = `${colName(colIndex)}${rowIndex + 1}`;
          return `<c r="${ref}" t="inlineStr"><is><t>${sanitizeXml(value)}</t></is></c>`;
        })
        .join("");
      return `<row r="${rowIndex + 1}">${cells}</row>`;
    })
    .join("");

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n`
    + `<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">`
    + `<sheetData>${body}</sheetData>`
    + `</worksheet>`;
};

export function exportToExcel({ rows = [], fileName = "export", sheetName = "Sheet1" }) {
  if (!Array.isArray(rows) || rows.length === 0) {
    throw new Error("Нет данных для выгрузки");
  }

  const safeSheetName = String(sheetName || "Sheet1").slice(0, 31);

  const entries = [
    {
      name: "[Content_Types].xml",
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n`
        + `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">`
        + `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>`
        + `<Default Extension="xml" ContentType="application/xml"/>`
        + `<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>`
        + `<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`
        + `<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>`
        + `</Types>`,
    },
    {
      name: "_rels/.rels",
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n`
        + `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">`
        + `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>`
        + `</Relationships>`,
    },
    {
      name: "xl/workbook.xml",
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n`
        + `<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">`
        + `<sheets><sheet name="${sanitizeXml(safeSheetName)}" sheetId="1" r:id="rId1"/></sheets>`
        + `</workbook>`,
    },
    {
      name: "xl/_rels/workbook.xml.rels",
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n`
        + `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">`
        + `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>`
        + `<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>`
        + `</Relationships>`,
    },
    {
      name: "xl/styles.xml",
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n`
        + `<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">`
        + `<fonts count="1"><font><sz val="11"/><name val="Calibri"/></font></fonts>`
        + `<fills count="1"><fill><patternFill patternType="none"/></fill></fills>`
        + `<borders count="1"><border/></borders>`
        + `<cellStyleXfs count="1"><xf/></cellStyleXfs>`
        + `<cellXfs count="1"><xf xfId="0"/></cellXfs>`
        + `</styleSheet>`,
    },
    {
      name: "xl/worksheets/sheet1.xml",
      data: buildSheetXml(rows),
    },
  ];

  const bytes = buildZip(entries);
  const blob = new Blob([bytes], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName.endsWith(".xlsx") ? fileName : `${fileName}.xlsx`;
  link.click();
  URL.revokeObjectURL(link.href);
}
